import getStore from "@utils/redux/store";
import { getURLParams } from "@utils/urlParams";
import { setFeatureRolloutType } from "@utils/redux/slices/persistentBrowserFlags";
import { appConfig } from "src/config";
import { capitalizeFirstLetter } from "src/primitive/string";
import { getMedFlagOverride } from "./userSegment/features";
import { CompoundedMicrodosePlanIneligibleStates } from "src/components/refactored-survey/question-sets/insurance-survey-questions/utils/insuranceConstants";
import { getSubdivision } from "./meristemContext";

function getTrafficPercentageParam(key: string) {
  const param = new URLSearchParams(window.location.search).get(key);
  if (param) {
    return parseFloat(param) / 100;
  }
  return undefined;
}

// Types
export enum RolloutUserType {
  CONTROL = "CONTROL",
  VARIATION = "VARIATION",
  HOLDBACK = "HOLDBACK",
  FORCED_ENABLED = "FORCED_ENABLED",
  FORCED_DISABLED = "FORCED_DISABLED",
  PAID_TRAFFIC = "PAID_TRAFFIC",
}

export interface RolloutConfig {
  featureName: string;
  enabled: boolean;
  trafficSplit: number;
  holdbackTraffic: number;
  autoAssign?: boolean;
  featureParamName?: string;
  restrictedRolloutStates?: Record<string, string>;
}

export interface RolloutState {
  rolloutType: RolloutUserType | null;
}

// Type for userData rollout storage
export interface UserDataRollouts {
  [featureName: string]: RolloutUserType | null;
}

// Base Rollout Class
export class FeatureRollout {
  private config: RolloutConfig;

  constructor(config: RolloutConfig) {
    this.config = {
      ...config,
      trafficSplit:
        getTrafficPercentageParam(
          `${config.featureName}TrafficSplitPercentage`
        ) ?? config.trafficSplit,
      holdbackTraffic:
        getTrafficPercentageParam(`${config.featureName}HoldbackPercentage`) ??
        config.holdbackTraffic,
      autoAssign: config.autoAssign ?? true,
      enabled:
        getMedFlagOverride(`${config.featureName}RolloutEnabled`) ??
        config.enabled,
      restrictedRolloutStates: config.restrictedRolloutStates ?? {},
    };
  }

  static create(config: RolloutConfig): FeatureRollout {
    return new FeatureRollout({
      ...config,
    });
  }

  private getForcedParam(): string | null {
    const forcedParamName = `enable${capitalizeFirstLetter(
      this.config.featureName
    )}`;
    return getURLParams()?.[forcedParamName] ?? null;
  }

  private getCurrentRolloutType(): RolloutUserType | null {
    const store = getStore();
    const { persistentBrowserFlags, userData } = store.getState();
    return (
      persistentBrowserFlags.featureRolloutTypes?.[this.config.featureName]
        ?.rolloutType ||
      (userData.featureRolloutTypes as UserDataRollouts)?.[
        this.config.featureName
      ] ||
      null
    );
  }

  private persistRolloutType(type: RolloutUserType): void {
    const store = getStore();
    const { persistentBrowserFlags } = store.getState();

    // Check and update persistentBrowserFlags if different
    const existingPersistentType =
      persistentBrowserFlags.featureRolloutTypes?.[this.config.featureName]
        ?.rolloutType;

    if (type !== existingPersistentType) {
      store.dispatch(
        setFeatureRolloutType({
          feature: this.config.featureName,
          rolloutType: type,
        })
      );
    }
  }

  private determineRolloutType(): RolloutUserType {
    // Check for forced parameter override
    const forcedParam = this.getForcedParam();
    if (forcedParam === "true") {
      return RolloutUserType.FORCED_ENABLED;
    }
    if (forcedParam === "false") {
      return RolloutUserType.FORCED_DISABLED;
    }

    // If feature is not enabled, return FORCED_DISABLED
    if (!this.isRolloutEnabled()) {
      return RolloutUserType.FORCED_DISABLED;
    }

    // Check for feature parameter
    if (this.isPaidTraffic()) {
      return RolloutUserType.PAID_TRAFFIC;
    }

    // Check for existing assignment
    const currentType = this.getCurrentRolloutType();
    if (currentType && currentType !== RolloutUserType.FORCED_DISABLED) {
      // When at 100% rollout, override cached state to serve feature to everyone
      if (
        this.config.holdbackTraffic === 0 &&
        this.config.trafficSplit === 1.0
      ) {
        return RolloutUserType.FORCED_ENABLED;
      }
      return currentType;
    }

    // Check holdback first
    if (Math.random() < this.config.holdbackTraffic) {
      return RolloutUserType.HOLDBACK;
    }

    // Then check traffic split
    if (Math.random() < this.config.trafficSplit) {
      return RolloutUserType.VARIATION;
    }

    // Default to control
    return RolloutUserType.CONTROL;
  }

  private setRolloutType(): RolloutUserType {
    const rolloutType = this.determineRolloutType();
    this.persistRolloutType(rolloutType);
    return rolloutType;
  }

  getRolloutStatus(): RolloutUserType {
    if (this.config.autoAssign) {
      return this.setRolloutType();
    }
    return this.getCurrentRolloutType();
  }

  isInPercentRollout(): boolean {
    const status = this.getRolloutStatus();
    return [
      RolloutUserType.FORCED_ENABLED,
      RolloutUserType.VARIATION,
      RolloutUserType.PAID_TRAFFIC,
    ].includes(status);
  }

  shouldEnableFeature(): boolean {
    return this.isRolloutEnabled() && this.isInPercentRollout();
  }

  isPaidTraffic(): boolean {
    const featureParam = getURLParams()?.feature;
    if (!featureParam || !this.config.featureParamName) {
      return false;
    }
    return featureParam === this.config.featureParamName;
  }

  private isEligibleByIPState(): boolean {
    // Paid traffic handles it's own state restrictions at the ad targeting level
    if (this.isPaidTraffic()) {
      return true;
    }

    // For non-paid traffic, check if state is allowed
    return !(getSubdivision() in this.config.restrictedRolloutStates);
  }

  isRolloutEnabled(): boolean {
    return this.config.enabled && this.isEligibleByIPState();
  }
}

const LIRAGLUTIDE_ROLLOUT: RolloutConfig = {
  featureName: "liraglutideMedPlans",
  enabled: appConfig.LIRAGLUTIDE_MED_PLANS_ENABLED,
  trafficSplit: appConfig.LIRAGLUTIDE_MED_PLANS_TRAFFIC_SPLIT,
  holdbackTraffic: appConfig.LIRAGLUTIDE_MED_PLANS_HOLDBACK_TRAFFIC,
  autoAssign: true,
};

export const getLiraglutideRollout = () =>
  FeatureRollout.create(LIRAGLUTIDE_ROLLOUT);

const MICRODOSE_ROLLOUT: RolloutConfig = {
  featureName: "microdose",
  enabled: appConfig.MICRODOSE_ENABLED,
  trafficSplit: appConfig.MICRODOSE_TRAFFIC_SPLIT,
  holdbackTraffic: appConfig.MICRODOSE_HOLDBACK_TRAFFIC,
  autoAssign: true,
  featureParamName: "microdose",
  restrictedRolloutStates: CompoundedMicrodosePlanIneligibleStates,
};

export const getMicrodoseRollout = () =>
  FeatureRollout.create(MICRODOSE_ROLLOUT);

const ELTV_SOA_GENERATION_ROLLOUT: RolloutConfig = {
  featureName: "eltvSoaGeneration",
  enabled: appConfig.ELTV_SOA_GENERATION_ENABLED,
  trafficSplit: appConfig.ELTV_SOA_GENERATION_TRAFFIC_SPLIT,
  holdbackTraffic: appConfig.ELTV_SOA_GENERATION_HOLDBACK_TRAFFIC,
  autoAssign: true,
  featureParamName: "eltvSoaGeneration",
};

export const getEltvSoaGenerationRollout = () =>
  FeatureRollout.create(ELTV_SOA_GENERATION_ROLLOUT);
